import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoIosCall } from "react-icons/io";
import { useMediaQuery } from "react-responsive";
import logo from "../assets/nav_logo.png";
import logo2 from "../assets/logo2.png";

const NAV_H = 82;

const navLinks = [
  { label: "Home",           id: "hero",    to: "/" },
  { label: "About Us",       id: "about",   to: "/about" },
  { label: "Products",       id: "product", to: "/product" },
  { label: "Services",       id: "service", to: "/service" },
  { label: "Contact",        id: "contact", to: "/contact" },
];

const Navbar = ({ openDemo }) => {
  const isDesktop = useMediaQuery({ query: "(min-width: 1024px)" });
  const [menuOpen, setMenuOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [isDarkBg, setIsDarkBg] = useState(true);
  const prevScrollY = useRef(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  // scroll handling
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      // Do not hide navbar if menu is open
      if (!menuOpen) {
        setVisible(y < prevScrollY.current || y < 10);
      }
      setScrolled(y > 80);
      prevScrollY.current = y;

      // Detect dark backgrounds
      let dark = false;
      const darkSections = document.querySelectorAll('.bg-slate-950, .bg-blue-900, [data-theme="dark"]');
      darkSections.forEach(sec => {
        const rect = sec.getBoundingClientRect();
        if (rect.top <= 41 && rect.bottom >= 41) {
          dark = true;
        }
      });
      if (y < 80 && location.pathname === "/") {
        dark = true;
      }
      setIsDarkBg(dark);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [menuOpen, location.pathname]);

  // lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // close menu on resize or route change
  useEffect(() => { if (isDesktop) setMenuOpen(false); }, [isDesktop]);
  useEffect(() => { setMenuOpen(false); }, [location]);

  // scroll spy for home page active section
  useEffect(() => {
    if (!isHome) { setActiveSection(""); return; }
    
    const onScrollActive = () => {
      const scrollPos = window.scrollY + NAV_H + 100;
      if (scrollPos < 500) {
        setActiveSection("hero");
      }
    };

    window.addEventListener("scroll", onScrollActive, { passive: true });
    setTimeout(onScrollActive, 100);
    
    return () => window.removeEventListener("scroll", onScrollActive);
  }, [isHome, location.pathname]);

  // check session storage for scroll targets
  useEffect(() => {
    if (!isHome) return;
    const target = sessionStorage.getItem("scrollTarget");
    if (!target) return;
    sessionStorage.removeItem("scrollTarget");
    setTimeout(() => {
      if (target === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
      const el = document.getElementById(target);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - NAV_H, behavior: "smooth" });
    }, 150);
  }, [isHome]);

  // smooth scroll
  const scrollToSection = useCallback((id) => {
    if (id === "hero" || !id) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
  }, []);

  // handle navigation clicks
  const handleNavClick = (link) => {
    setMenuOpen(false);
    if (link.to === "/" && isHome) {
      scrollToSection("hero");
    } else {
      navigate(link.to);
    }
  };

  const isActive = (link) => {
    if (isHome) return activeSection === link.id;
    return location.pathname === link.to || (link.to !== "/" && location.pathname.startsWith(link.to));
  };

  const overDark = isDarkBg;
  const desktopTextClass = overDark
    ? "text-white/90 hover:text-white"
    : "text-slate-700 hover:text-blue-700";
  const desktopActiveClass = overDark
    ? "border-white text-white"
    : "border-blue-700 text-blue-700";
  const phoneTextClass = overDark ? "text-white/90" : "text-slate-700";
  const navShellClass = overDark
    ? "border-white/10 bg-slate-950/10 shadow-none"
    : "border-slate-200/70 bg-white/80 shadow-[0_2px_20px_rgba(0,0,0,0.08)]";

  const handleLogoClick = () => {
    setMenuOpen(false);
    if (isHome) {
      scrollToSection("hero");
      return;
    }
    navigate("/");
  };

  return (
    <>
      <nav
        className={`fixed inset-x-0 top-0 z-[1000] h-[82px] border-b backdrop-blur-sm transition-all duration-300 ${
          visible || menuOpen ? "translate-y-0" : "-translate-y-full"
        } ${navShellClass}`}
      >
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={handleLogoClick}
            className="relative z-[1100] shrink-0 cursor-pointer border-0 bg-transparent p-0"
          >
            <img src={overDark ? logo2 : logo} alt="BFI Logo" className="block h-12 w-auto sm:h-14" />
          </button>

          {isDesktop && (
            <ul className="flex flex-1 items-center justify-center gap-1">
              {navLinks.map((link) => {
                const active = isActive(link);
                return (
                  <li key={link.id}>
                    <button
                      type="button"
                      onClick={() => handleNavClick(link)}
                      className={`border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap transition ${
                        active
                          ? desktopActiveClass
                          : `border-transparent ${desktopTextClass}`
                      }`}
                    >
                      {link.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {isDesktop && (
            <div className="flex shrink-0 items-center gap-4">
              <a
                href="tel:+919769847865"
                className={`flex items-center gap-1.5 whitespace-nowrap text-sm font-medium transition ${
                  overDark
                    ? "text-white/90 hover:text-white"
                    : "text-slate-700 hover:text-blue-700"
                }`}
              >
                <IoIosCall className="shrink-0 text-lg text-600" />
                +91 9769847865
              </a>
              <button
                type="button"
                onClick={openDemo}
                className="whitespace-nowrap rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-[0_2px_10px_rgba(29,78,216,0.35)] transition hover:bg-blue-800 hover:shadow-[0_4px_14px_rgba(29,78,216,0.45)]"
              >
                Request a Demo
              </button>
            </div>
          )}

          {!isDesktop && (
            <button
              type="button"
              onClick={() => setMenuOpen(p => !p)}
              className="relative z-[1100] flex h-10 w-10 flex-col items-center justify-center gap-1.5 border-0 bg-transparent"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              <span
                className={`block h-0.5 w-6 rounded-full transition ${
                  menuOpen
                    ? "translate-y-2 rotate-45 bg-white"
                    : `${phoneTextClass} bg-current`
                }`}
              />
              <span
                className={`block h-0.5 w-6 rounded-full transition ${
                  menuOpen ? "opacity-0" : `${phoneTextClass} bg-current`
                }`}
              />
              <span
                className={`block h-0.5 w-6 rounded-full transition ${
                  menuOpen
                    ? "-translate-y-2 -rotate-45 bg-white"
                    : `${phoneTextClass} bg-current`
                }`}
              />
            </button>
          )}
        </div>
      </nav>

      {!isDesktop && (
        <button
          type="button"
          onClick={() => setMenuOpen(false)}
          aria-label="Close mobile menu overlay"
          className={`fixed inset-0 z-[1040] border-0 bg-slate-950/40 p-0 backdrop-blur-[2px] transition ${
            menuOpen ? "visible opacity-100" : "invisible opacity-0"
          }`}
        />
      )}

      {!isDesktop && (
        <div
          id="mobile-menu"
          className={`fixed inset-y-0 right-0 z-[1050] flex w-[min(86vw,320px)] flex-col overflow-y-auto border-l border-white/10 bg-slate-950/80 px-6 pb-8 pt-28 shadow-xl backdrop-blur-2xl transition-transform duration-500 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col items-center text-center">
            {navLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => handleNavClick(link)}
                className={`w-full border-b border-white/10 py-4 text-center text-lg font-semibold transition ${
                  isActive(link)
                    ? "text-blue-300"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {link.label}
              </button>
            ))}

            <a
              href="tel:+919769847865"
              className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-white/90 transition hover:text-white"
            >
              <IoIosCall className="text-xl text-orange-500" />
              +91 9769847865
            </a>

            <button
              type="button"
              onClick={() => { setMenuOpen(false); openDemo(); }}
              className="mt-6 w-full rounded-xl bg-blue-700 px-5 py-4 text-center text-base font-semibold text-white shadow-[0_8px_24px_rgba(29,78,216,0.3)] transition hover:bg-blue-800"
            >
              Request a Demo
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
